import api from '../../../api/endpoints';
import { sessionCache, sessionCached } from '../../../utils/cache';
import { dataFetch, dataDelete, dataPost } from '../../../api/data-fetch';
import config from '../config';
import t from './types';

const init = async ({ commit, dispatch }, [hash, eventId, eventRunners]) => {
  dispatch('FormFinder/fetchFields', hash);

  if (eventId) {
    commit(t.EVENT_FILTER, eventId);
  }
  if (eventRunners) {
    commit(t.EVENT_RUNNERS, eventRunners);
  }
  if (hash) {
    dispatch('FormFinder/fetchResults', hash);
  } else {
    commit(t.STATUS, t.STARTED);
  }
};

const fetchFields = async ({ commit }, hash = null) => {
  let fieldsData;

  if (hash || !(fieldsData = sessionCached('FF/fieldsNew'))) {
    const params = {};
    if (hash) params.hash = hash;
    fieldsData = await dataFetch(api.FORM_FINDER_FIELDS, { params });
    sessionCache('FF/fieldsNew', fieldsData, config.cacheTime);
  }
  commit(t.FIELDS, fieldsData);
  commit(t.INFO, fieldsData.info);
  commit(t.PRESETS, fieldsData.presets);
  commit(t.TOTAL_COUNT, [fieldsData.totalUpcomingSelections, fieldsData.dayRange]);
};

const fetchResults = async ({ commit, dispatch, getters }, hash) => {
  let eventFilter, results;
  const url = `${api.FORM_FINDER}`;
  const metrics = getters['FormFinder/metrics'];
  const activeMetricList = getters['FormFinder/fields'].map(field => field.metricList);
  const queryFilterList = Object.values(metrics)
    .filter(metric => {
      return metric.value !== 0 && isMetricActive(activeMetricList, metric.id);
    })
    .map(metric => {
      return {
        name: metric.setting,
        metric: metric.name,
        value: metric.value,
        range: metric.range,
        type: metric.type,
      };
    });

  const queryParam = JSON.stringify({
    filters: queryFilterList,
  });

  const params = hash ? { hash } : { q: queryParam };
  if ((eventFilter = getters['FormFinder/eventFilter'])) {
    params.events = eventFilter;
  }

  if (!queryFilterList.length && !hash) {
    commit(t.STATUS, t.STARTED);
    return;
  }

  commit(t.STATUS, t.FETCH_RESULTS);
  commit(t.QUERY, queryParam);

  const cacheKey = JSON.stringify(params);
  if (!(results = sessionCached(`FF/${cacheKey}`))) {
    // new search
    try {
      results = await dataFetch(url, { params });
      sessionCache(`FF/${cacheKey}`, results, config.cacheTime);
    } catch (error) {
      commit(t.STATUS, t.ERROR_FETCH);
    }
  }

  return new Promise(resolve => {
    if (!results) return resolve();

    if (getters['FormFinder/query'] === queryParam) {
      commit(t.HASH, results.hash);
      commit(t.RESULTS, { events: results.events, hash: results.hash });
      commit(t.STATUS, results.events.length ? t.RESULTS : t.RESULTS_NONE);

      dispatch('BestOdds/fetchEventOdds', results.events.map(event => event.id));

      return resolve(results.hash);
    }

    return resolve();
  });
};

const updateFilter = ({ commit }, payload) => {
  if (payload && payload.length) {
    commit(t.FIELD, payload);
  }
};

const search = async ({ dispatch }, hash = null) => {
  // if hash given, need to refresh form filters
  if (hash) {
    dispatch('FormFinder/fetchFields', hash);
  }
  return await dispatch('FormFinder/fetchResults', hash);
};

const fetchPresetData = async ({ commit }, { env = 'prod' }) => {
  try {
    const url = `${api.FORM_FINDER_PRESET.PRESET({ env }).ENDPOINT_FILTERS}`;
    const apikey = `${api.FORM_FINDER_PRESET.PRESET({ env }).APIKEY}`;
    const config = {
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/json',
        'x-api-key': apikey,
      },
      withCredentials: true,
    };
    const settings = await dataFetch(url, config);
    if (settings) {
      const presetData = settings.data;
      commit('SET_PRESET_DATA', { presetData });
      commit('SET_LOADING_STATUS', { isLoading: false });
      commit('SET_MODAL_STATUS', { showModal: false });
    }
  } catch (error) {
    console.log(error);
  }
};

const addPresetData = async ({ state, commit }, { env = 'prod', presetName }) => {
  try {
    const query = state.query ? JSON.parse(state.query) : '';
    const payload = {
      name: presetName,
      filters: query ? query.filters : '',
    };

    const url = `${api.FORM_FINDER_PRESET.PRESET({ env }).ENDPOINT_FILTERS}`;
    const apikey = `${api.FORM_FINDER_PRESET.PRESET({ env }).APIKEY}`;
    const config = {
      headers: {
        Accept: '*/*',
        'Cache-Control': 'no-cache',
        'x-api-key': apikey,
        'Content-Type': 'text/plain',
      },
    };
    const data = {
      data: JSON.stringify(payload),
    };
    const result = await dataPost(url, data, config);
    const { status } = result;

    if (status === 200) {
      commit(t.PRESET, presetName);
    }
  } catch (error) {
    console.error(error);
  }
};

const deletePresetData = async (state, { env = 'prod', deletedPresetIds }) => {
  try {
    const apikey = `${api.FORM_FINDER_PRESET.PRESET({ env }).APIKEY}`;
    const config = {
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/json',
        'x-api-key': apikey,
      },
      withCredentials: true,
    };
    const promises = deletedPresetIds.map(async presetId => {
      const url = `${api.FORM_FINDER_PRESET.PRESET({ env }).ENDPOINT_FILTERS}${presetId}`;
      return await dataDelete(url, config);
    });
    return Promise.all(promises).then(function() {
      return true;
    });
  } catch (error) {
    console.log(error);
  }
};

function isMetricActive(activeMetricList, metricId) {
  let isActive = false;

  for (const metricList of activeMetricList) {
    if (metricList.includes(metricId)) {
      isActive = true;
      break;
    }
  }

  return isActive;
}

export default {
  init,
  fetchFields,
  fetchResults,
  search,
  updateFilter,
  addPresetData,
  fetchPresetData,
  deletePresetData,
};
